import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppBar, Toolbar, Container, Box, Stack, Typography, Button } from "@mui/material";
import { Storefront as StorefrontIcon } from "../../icons";
import { Store as StoreIcon } from "../../icons";
import { AddBusiness as AddBusinessIcon } from "../../icons";
import { ArrowBack as ArrowBackIcon } from "../../icons";
import { T } from "../Translate";
import { barColors, brandColors } from "../../theme";
import { pageContentSx } from "../../pageLayout";

const QUICK_LINKS = [
  { label: <T>Guía</T>, path: "/stores", icon: <StorefrontIcon fontSize="small" /> },
  { label: <T>Mis tiendas</T>, path: "/my-stores", icon: <StoreIcon fontSize="small" /> },
  { label: <T>Crear tienda</T>, path: "/stores/create", icon: <AddBusinessIcon fontSize="small" /> },
];

// Nota: `maxWidth` queda como prop por compatibilidad con quienes ya la pasan,
// pero el contenido siempre se centra a PAGE_CONTENT_MAX_WIDTH (980px, ver
// pageLayout.js) — el resto de la app usa ese mismo ancho fijo, no los
// breakpoints nombrados de MUI ("md"/"lg").
const StoresAppBar = ({ title, subtitle, quickNav = true }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => navigate(-1);

  // El activo se marca con un relleno sólido (accent) + texto que contraste
  // con ese relleno, no con un tinte translúcido: antes usaba el mismo
  // barColors.activeBg tanto para el fondo de toda la fila como para el
  // "seleccionado", así que la pestaña activa casi no se distinguía del
  // resto (mismo tinte encima del mismo tinte).
  const activeTextColor = brandColors.mode === "light" ? "#ffffff" : "#0a0a1a";

  const navButtonSx = (active) => ({
    flex: { xs: "0 0 auto", sm: 1 },
    whiteSpace: "nowrap",
    minWidth: { xs: 0, sm: "auto" },
    px: { xs: 1, sm: 2 },
    py: 0.75,
    borderRadius: 2,
    "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 } },
    fontSize: { xs: "0.75rem", sm: "0.875rem" },
    color: active ? activeTextColor : barColors.textMuted,
    bgcolor: active ? brandColors.accent : "transparent",
    fontWeight: active ? 700 : 500,
    transition: "background-color 0.15s ease, color 0.15s ease",
    "&:hover": {
      color: active ? activeTextColor : barColors.text,
      bgcolor: active ? brandColors.accent : barColors.activeBg,
    },
  });

  // En mobile solo "Atrás" queda como ícono: las pestañas usan íconos de
  // tienda muy parecidos entre sí y sin texto no se distinguían.
  const navLabelSx = { display: { xs: "none", sm: "inline" } };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        background: barColors.background,
        color: barColors.text,
        borderBottom: `1px solid ${barColors.border}`,
      }}
    >
      <Container maxWidth={false} disableGutters sx={pageContentSx}>
        <Toolbar disableGutters sx={{ minHeight: 64, gap: 1.5 }}>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: barColors.textMuted,
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Toolbar>
        <Box sx={{ borderTop: `1px solid ${barColors.border}` }}>
          <Stack direction="row" sx={{ overflowX: "auto", py: 0.75, px: 0.5, gap: 0.75 }}>
            {/* navButtonSx pone flex:1 a partir de "sm" para que varios
                botones repartan el ancho de la fila como pestañas — pero
                cuando "Atrás" está solo (quickNav=false) eso lo estira a
                todo el ancho, y como el contenido de un Button se centra
                por defecto, el texto termina flotando en el medio de la
                fila en vez de pegado a la izquierda junto al título. */}
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ ...navButtonSx(false), ...(!quickNav && { flex: "0 0 auto" }) }}
            >
              <Box component="span" sx={navLabelSx}>
                <T>Atrás</T>
              </Box>
            </Button>
            {quickNav &&
              QUICK_LINKS.map((link) => (
                <Button
                  key={link.path}
                  startIcon={link.icon}
                  onClick={() => navigate(link.path)}
                  sx={{ ...navButtonSx(location.pathname === link.path), "& .MuiButton-startIcon": { mr: 0.75 } }}
                >
                  {link.label}
                </Button>
              ))}
          </Stack>
        </Box>
      </Container>
    </AppBar>
  );
};

export default StoresAppBar;
